"use client"

// components/RootLayout.tsx
import { ContentfulLivePreview } from "@contentful/live-preview"
import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react"
import clsx from "clsx"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useEffect, useMemo, useState } from "react"

import gtmPageView from "@/helpers/gtm"
import GlobalPencilBanner from "@/ui/components/banners/GlobalPencilBanner"

import type { AppContextInterface } from "./AppContext"
import AppContext, { initialAppContext, Context } from "./AppContext"
import Header from "./Header"
import ModalContainer from "./ModalContainer"
import Footer from "./navigation/Footer"
import PageThemeContext, { getPageTheme } from "./page-theme/PageThemeContext"

interface RootLayoutProps {
  fontClass?: string
  children?: React.ReactNode
}

const PAGES_TO_HIDE_BANNER = [
  "/impact/",
  "/impact/impact-report/",
  "/impact/sustainability/",
  "/impact/ubri/",
  "/how-to-identify-crypto-scams/",
  "/events/swell/",
  "/events/swell/agenda/",
  "/events/swell/faq/",
  "/events/swell/speakers/",
]

const RootLayout: React.FC<RootLayoutProps> = ({ fontClass, children }) => {
  const pathname = usePathname()
  const [context, setContext] = useState<AppContextInterface>({
    ...initialAppContext,
    // OPTIMIZATION: Set initial banner visibility based on current pathname to prevent flash
    //
    // Problem: If we always start with isVisible: true, there's a brief flash on pages
    // where the banner should be hidden because:
    // 1. Component renders with banner visible
    // 2. useEffect runs after render and hides the banner
    // 3. This creates a visible flash/flicker on initial page load
    //
    // Solution: Calculate the correct initial visibility state immediately based on
    // the current pathname, so the banner renders with the correct state from the start.
    // This eliminates the client-side delay and provides a smooth user experience.
    globalPencilBanner: {
      isVisible: !PAGES_TO_HIDE_BANNER.includes(pathname),
    },
  })
  const contextValue = useMemo(
    () => new Context(context, setContext),
    [context],
  )

  useEffect(() => {
    ContentfulLivePreview.init({ locale: "en-US" })
    // Initialize your Contentful live preview settings here
  }, [])

  useEffect(() => {
    // Update banner visibility based on pathname changes
    const shouldHideBanner = PAGES_TO_HIDE_BANNER.includes(pathname)
    contextValue.setGlobalPencilBanner(!shouldHideBanner)

    const props = {
      page_title: pathname || null,
    }
    gtmPageView(props)
    // adding contextValue as a dependency will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (context.currentLayout.searchIsOpen) {
        document.body.classList.add("scroll-lock")
      } else {
        document.body.classList.remove("scroll-lock")
      }
    }
  }, [context.currentLayout.searchIsOpen])

  return (
    // the text-gray-2.5 is needed to make sure certain pages aren't broken
    // because of the addition of global styles that were added in _template-single.scss
    // we couldn't remove the global styles, which is the ideal solution,
    // since it broke too much styling on some pages, so this is the interim solution */}
    <div
      id="next-body"
      className={clsx(
        "relative text-gray-2.5 bg-gray-90",
        { "overflow-x-clip": context.bioModal.modalOpen },
        context.currentLayout.className,
        fontClass,
        context.currentLayout.searchIsOpen
          ? "overflow-hidden"
          : "overflow-auto",
      )}
    >
      <AppContext.Provider value={contextValue}>
        <PageThemeContext.Provider
          value={useMemo(
            () => ({ pageTheme: getPageTheme(pathname) }),
            [pathname],
          )}
        >
          <ContentfulLivePreviewProvider
            enableLiveUpdates
            locale="en-US" // Specify the locale you are working with
          >
            <ModalContainer />
            {contextValue.globalPencilBanner.isVisible && (
              <GlobalPencilBanner
                copy="RIPPLE SWELL REGISTRATION IS OPEN"
                buttonAttrs={{
                  to: "https://register.ripple.com/swell2025registration/begin/?utm_source=ripplewebsite&utm_medium=popup_banner&utm_campaign=crossbu-eventho-swell-glb-2025-q4_ripplewebsite_ari_artl_bf_rsvp&utm_content=btn_english_swellpencilbanner",
                  children: "Secure your spot today",
                }}
                backgroundLayout={
                  <>
                    {/* Left pencil background - Mobile */}
                    <div className="absolute top-0 left-0 z-0 h-full md:hidden">
                      <Image
                        src="/backgrounds/swell-xs-left.png"
                        alt=""
                        width={600}
                        height={86}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    {/* Right pencil background - Mobile */}
                    <div className="absolute top-0 right-0 z-0 h-full md:hidden">
                      <Image
                        src="/backgrounds/swell-xs-right.png"
                        alt=""
                        width={600}
                        height={90}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    {/* Left pencil background - Desktop */}
                    <div className="absolute top-0 left-0 z-0 h-full hidden md:block">
                      <Image
                        src="/backgrounds/swell-pencil-left.png"
                        alt=""
                        width={516}
                        height={80}
                        className="h-full w-auto object-cover"
                      />
                    </div>

                    {/* Right pencil background - Desktop */}
                    <div className="absolute top-0 right-0 z-0 h-full hidden md:block">
                      <Image
                        src="/backgrounds/swell-pencil-right.png"
                        alt=""
                        width={516}
                        height={80}
                        className="h-full w-auto object-cover"
                      />
                    </div>
                  </>
                }
              />
            )}
            <Header />
            <main
              id="site-content"
              role="main"
              className="relative z-0 bg-gray-90"
            >
              {children}
            </main>
            <Footer />
          </ContentfulLivePreviewProvider>
        </PageThemeContext.Provider>
      </AppContext.Provider>
    </div>
  )
}

export default RootLayout
